<template>
  <div class="py-5 md:px-5">
    <PageHeader class="max-w-4xl" title="Edit Order" :back-step="true" />
    
    <div class="max-w-4xl mx-auto">
      <div class="card">
        <Loading v-if="loading" />
        <form @submit.prevent="saveOrder()" v-else>
          <!-- Errors -->
          <div class="p-6" v-if="form.uploading_error || form.geocoding_error">
            <div class="flex items-center p-4 text-red-800 bg-red-100 rounded">
              <div class="flex-none mr-3 opacity-75">
                <fa :icon="['far', 'exclamation-circle']" size="lg" />
              </div>
              <div class="flex-grow">
                <div class="font-bold">Error(s) while uploading this order to WEBFLEET:</div>
                <div>{{ form.uploading_error }}</div>
                <div>{{ form.geocoding_error }}</div>
              </div>
              <div>
                <button type="button" class="btn btn-red" v-submitting="submitting" @click.prevent="retryUpload">
                  Retry Upload
                </button>
              </div>
            </div>
          </div>

          <!-- Order -->
          <div class="grid p-6 lg:grid-cols-2 gap-x-4">
            <fieldset class="form-group">
              <label class="form-label">Order Type</label>
              <radio-group 
                v-model="form.ordertype"
                :options="[
                  { value: 1, label: 'Service' },
                  { value: 2, label: 'Pickup' },
                  { value: 3, label: 'Delivery' }
                ]" />
            </fieldset>
            <fieldset class="form-group">
              <label class="form-label">Order Number</label>
              <input type="text" class="form-input" v-model="form.orderid">
              <validations :errors="errors" field="orderid" />
            </fieldset>
            <fieldset class="form-group">
              <label class="form-label">Order Date</label>
              <input type="text" class="form-input" v-model="form.orderdate">
              <validations :errors="errors" field="orderdate" />
            </fieldset>
            <fieldset class="form-group">
              <label class="form-label">Order Time</label>
              <input type="text" class="form-input" v-model="form.ordertime" v-cleave="{ time: true, timePattern: ['h', 'm'], timeFormat: '24' }">
              <validations :errors="errors" field="ordertime" />
            </fieldset>
          </div>
          
          <!-- Customer -->
          <div class="px-6 py-2 text-xs tracking-wider text-gray-500 uppercase border-t bg-gray-50">
            Customer Details
          </div>
          <div class="grid p-6 lg:grid-cols-3 gap-x-4">
            <fieldset class="form-group">
              <label class="form-label">Customer Name</label>
              <input type="text" class="form-input" v-model="form.customer_name">
              <validations :errors="errors" field="customer_name" />
            </fieldset>
            <fieldset class="form-group">
              <label class="form-label">Customer Phone</label>
              <input type="text" class="form-input" v-model="form.customer_phone">
              <validations :errors="errors" field="customer_phone" />
            </fieldset>
            <fieldset class="form-group">
              <label class="form-label">Customer Email</label>
              <input type="text" class="form-input" v-model="form.customer_email">
              <validations :errors="errors" field="customer_email" />
            </fieldset>
          </div>

          <!-- Contact -->
          <div class="px-6 py-2 text-xs tracking-wider text-gray-500 uppercase border-t bg-gray-50">
            Contact Details
          </div>
          <div class="grid p-6 lg:grid-cols-3 gap-x-4">
            <fieldset class="form-group">
              <label class="form-label">Contact Name</label>
              <input type="text" class="form-input" v-model="form.contact_name">
              <validations :errors="errors" field="contact_name" />
            </fieldset>
            <fieldset class="form-group">
              <label class="form-label">Contact Phone</label>
              <input type="text" class="form-input" v-model="form.contact_phone">
              <validations :errors="errors" field="contact_phone" />
            </fieldset>
            <fieldset class="form-group">
              <label class="form-label">Contact Email</label>
              <input type="text" class="form-input" v-model="form.contact_email">
              <validations :errors="errors" field="contact_email" />
            </fieldset>
          </div>

          <!-- Address -->
          <div class="px-6 py-2 text-xs tracking-wider text-gray-500 uppercase border-t bg-gray-50">
            Address
          </div>
          <div class="p-6">
            <fieldset class="col-span-2 form-group">
              <div class="flex justify-between">
                <label class="form-label">Address</label>
                <div class="text-xs text-gray-500" >
                  <template v-if="form.latitude && form.longitude">
                    <a :href="`https://www.google.com/maps/place/${form.latitude},${form.longitude}/`" target="_blank">
                      Latitude: {{ form.latitude }}, Longitude: {{ form.longitude }}
                    </a>
                  </template>
                  <template v-else>
                    No Coordinates Yet
                  </template>
                </div>
              </div>
              <AddressAutocomplete 
                input-class="form-input"
                id="addressAutocomplete"
                @placechanged="getAddressData" />
              <validations :errors="errors" field="formatted_address" />
            </fieldset>
            <Collapse :active="form.street.length > 0" :duration="200">
              <div class="grid px-4 bg-gray-100 border rounded gap-x-4 lg:grid-cols-3">
                <div class="py-2 mb-2 text-xs tracking-wider text-gray-500 uppercase lg:col-span-3">
                  Formatted Address
                </div>
                <fieldset class="form-group lg:col-span-3">
                  <label class="form-label">Street</label>
                  <input type="text" class="form-input" autocomplete="disabled" v-model="form.street">
                  <validations :errors="errors" field="street" />
                </fieldset>
                <fieldset class="form-group">
                  <label class="form-label">City</label>
                  <input type="text" class="form-input" autocomplete="disabled" v-model="form.city">
                  <validations :errors="errors" field="city" />
                </fieldset>
                <fieldset class="form-group">
                  <label class="form-label">ZIP</label>
                  <input type="text" class="form-input" autocomplete="disabled" v-model="form.zip">
                  <validations :errors="errors" field="zip" />
                </fieldset>
                <fieldset class="form-group">
                  <label class="form-label">Country</label>
                  <country-select class="form-select" v-model="form.country" /> 
                  <validations :errors="errors" field="country" />
                </fieldset>
              </div>
            </Collapse>
          </div>

          <!-- Notes -->
          <div class="px-6 py-2 text-xs tracking-wider text-gray-500 uppercase border-t bg-gray-50">
            Notes
          </div>
          <div class="p-6">
            <fieldset class="form-group">
              <label class="form-label">Notes</label>
              <input type="text" class="form-input" v-model="form.ordertext">
              <validations :errors="errors" field="ordertext" />
            </fieldset>
          </div>

          <!-- Extras -->
          <div class="px-6 py-2 text-xs tracking-wider text-gray-500 uppercase border-t bg-gray-50">
            Extras
          </div>
          <div class="p-6">
            <fieldset class="form-group">
              <label class="form-label">Reference</label>
              <input type="text" class="form-input" v-model="form.reference">
              <validations :errors="errors" field="reference" />
            </fieldset>
          </div>

          <!-- Products -->
          <template v-if="address && address.products">
            <div class="px-6 py-2 text-xs tracking-wider text-gray-500 uppercase border-t bg-gray-50">
              Products
            </div>
            <div class="divide-y">
              <div v-for="product in address.products" :key="product.uuid" class="flex items-center justify-between px-6 py-3">
                <div class="flex items-center col-span-2 space-x-4">
                  <div class="flex items-center justify-center flex-none w-12 h-12 overflow-hidden border rounded bg-gray-50">
                    <img v-if="product.image" :src="product.image" :alt="product.name" class="object-cover h-full">
                    <fa v-else :icon="['far', 'cube']" class="text-lg text-gray-300" />
                  </div>
                  <div>
                    <div class="flex items-center font-semibold">
                      {{ product.name }} 
                    </div>
                    <div class="text-gray-500">
                      <currency :value="product.price" :currency-code="account.currency" :subunits-value="true" />
                    </div>
                  </div>
                </div>
                <div class="flex">
                  <div class="mr-6">
                    &times; {{ product.quantity }}
                  </div>
                  <currency class="font-medium" :value="product.subtotal" :currency-code="account.currency" :subunits-value="true" />
                </div>
              </div>
            </div>
          </template>

          <!-- Addons -->
          <template v-if="address && address.addons">
            <div class="px-6 py-2 text-xs tracking-wider text-gray-500 uppercase border-t bg-gray-50">
              Addons
            </div>
            <div class="divide-y">
              <div v-for="addon in address.addons" :key="addon.uuid" class="flex items-center justify-between px-6 py-3">
                <div class="flex items-center col-span-3 space-x-4">
                  &check; {{ addon.name }} 
                </div>
                <div class="font-medium">
                  <currency :value="addon.price" :currency-code="account.currency" :subunits-value="true" />
                </div>
              </div>
            </div>
          </template>
            
          <div class="p-6 border-t">
            <button type="submit" class="btn btn-primary" v-submitting="submitting">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import AddressAutocomplete from '@/components/AddressAutocomplete'

export default {
  name: 'EditOrder',

  metaInfo: {
    title: 'Edit Order'
  },

  components: {
    AddressAutocomplete
  },

  async created () {
    await this.getOrder()
  },

  computed: {
    ...mapGetters(['account']),
    
    address () {
      return this.form.booking_destination_id ? this.form.booking_destination : this.form.booking_origin
    }
  },

  data () {
    return {
      loading: true,
      submitting: false,
      errors: [],
      form: {}
    }
  },

  methods: {
    async getOrder () {
      try {
        const { data } = await this.$api.get(`account/${this.$route.params.account}/orders/${this.$route.params.id}`)
        this.form = data.order
        this.loading = false
      } catch (error) {
        console.error(error)
      }
    },

    async saveOrder () {
      try {
        this.submitting = true
        await this.$api.patch(`account/${this.$route.params.account}/orders/${this.$route.params.id}`, this.form)
        this.$router.push({ name: 'OrderList' })
      } catch (error) {
        console.error(error)
        this.errors = error.response.data.errors ? error.response.data.errors : []
      } finally {
        this.submitting = false
      }
    },

    async retryUpload () {
      try {
        this.submitting = true
        await this.$api.patch(`account/${this.$route.params.account}/orders/${this.$route.params.id}`, {
          ...this.form,
          geocoding_error: null,
          uploading_error: null
        })
        this.$router.push({ name: 'OrderList' })
      } catch (error) {
        console.error(error)
        this.errors = error.response.data.errors ? error.response.data.errors : []
      } finally {
        this.submitting = false
      }
    },

    getAddressData ({ addressData, placeResultData }) {
      for (let component of placeResultData.address_components) {
        if (component.types.includes('country')) {
          this.form.country = component.short_name
        }
      }
      this.form.formatted_address = placeResultData.formatted_address
      this.form.city = addressData.locality
      this.form.zip = addressData.postal_code
      this.form.latitude = parseFloat(addressData.latitude).toFixed(6)
      this.form.longitude = parseFloat(addressData.longitude).toFixed(6)
      this.form.street = placeResultData.formatted_address.split(',')[0] + ',' + placeResultData.formatted_address.split(',')[1]
    }
  }
}
</script>